/**
 * The pieces the two-factor screens share (redesign §16, §37.5): the six-digit
 * code field, the numbered step kicker, the copyable key and the recovery-codes
 * frame. Enrolment draws them on Profile and in the middle of a sign-in; the
 * verify step draws the code field.
 */
import { useEffect, useRef, useState } from 'react';
import type React from 'react';
import { color, font, radius } from '../../theme/tokens';
import { CheckMark } from '../../components/icons';
import { ApiError } from '../../lib/api';

/**
 * The Worker's « That code is not right. … or a recovery code. » said for the
 * screen it lands on: enrolment has no recovery code to offer yet (14c, 37c),
 * and the recovery-code mode names what was typed (13f). Any other refusal —
 * an expired sign-in, too many tries — keeps the Worker's own sentence.
 */
export function codeRefusal(failure: unknown, kind: 'enrol' | 'verify' | 'recovery'): string {
  if (!(failure instanceof ApiError)) return 'That did not work.';
  if (!failure.message.startsWith('That code is not right')) return failure.message;
  if (kind === 'enrol') return 'That code is not right. Use the six digits your app shows now.';
  if (kind === 'recovery') return 'That recovery code is not right, or it was already used.';
  return failure.message;
}

/** « Copy » that reads « Copied » for 1.6 s once the clipboard took the text. */
export function useCopied(): [boolean, (text: string) => void] {
  const [copied, setCopied] = useState(false);
  const timer = useRef(0);
  useEffect(() => () => window.clearTimeout(timer.current), []);
  const copy = (text: string): void => {
    void navigator.clipboard?.writeText(text).then(() => {
      setCopied(true);
      window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => setCopied(false), 1600);
    });
  };
  return [copied, copy];
}

/**
 * The six digits (§16): mono 20, tabular, spaced .3 em, numeric keypad and the
 * one-time-code autofill. Anything but a digit is dropped as it is typed, and
 * the sixth digit hands the code to `onComplete` — no button press needed.
 */
export function CodeInput({
  id,
  value,
  onChange,
  onComplete,
  invalid,
  readOnly,
  describedBy,
  autoFocus
}: {
  id: string;
  value: string;
  onChange: (digits: string) => void;
  onComplete?: (code: string) => void;
  invalid?: boolean;
  readOnly?: boolean;
  describedBy?: string;
  autoFocus?: boolean;
}): React.JSX.Element {
  return (
    <input
      id={id}
      value={value}
      autoFocus={autoFocus}
      readOnly={readOnly}
      inputMode="numeric"
      autoComplete="one-time-code"
      pattern="[0-9]*"
      maxLength={6}
      aria-invalid={invalid || undefined}
      aria-describedby={describedBy}
      placeholder="123456"
      onChange={(event) => {
        const digits = event.target.value.replace(/\D+/g, '').slice(0, 6);
        onChange(digits);
        if (digits.length === 6 && digits !== value) onComplete?.(digits);
      }}
      style={{
        width: '100%',
        boxSizing: 'border-box',
        height: 44,
        padding: '0 12px',
        fontFamily: font.mono,
        fontSize: 20,
        fontVariantNumeric: 'tabular-nums',
        letterSpacing: '.3em',
        border: `1px solid ${invalid ? color.red : color.lineInput}`,
        borderRadius: radius.lg,
        background: color.surface,
        color: color.ink
      }}
    />
  );
}

/**
 * A step's numbered kicker (§37.5): teal while it is the step at hand, grey
 * with a check once done — a done step is folded to this line.
 */
export function StepKicker({ children, done }: { children: string; done?: boolean }): React.JSX.Element {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        fontFamily: font.mono,
        fontSize: 10.5,
        letterSpacing: '.14em',
        textTransform: 'uppercase',
        color: done ? color.inkSubtle : color.accentText
      }}
    >
      {done && <CheckMark size={14} />}
      {children}
    </div>
  );
}

const SMALL_BUTTON: React.CSSProperties = {
  flex: 'none',
  height: 28,
  padding: '0 11px',
  border: `1px solid ${color.line}`,
  borderRadius: radius.chip,
  background: color.surface,
  fontFamily: 'inherit',
  fontSize: 12.5,
  color: color.inkBody,
  cursor: 'pointer'
};

/** The secret, read-only and copyable (§3): mono, selected whole on focus, « Copy » beside it. */
export function CopyableKey({ label, value }: { label: string; value: string }): React.JSX.Element {
  const [copied, copy] = useCopied();
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <input
        readOnly
        value={value}
        aria-label={label}
        onFocus={(event) => event.currentTarget.select()}
        style={{
          flex: 1,
          minWidth: 0,
          height: 32,
          padding: '0 10px',
          border: `1px solid ${color.line}`,
          borderRadius: 8,
          background: color.surface,
          fontFamily: font.mono,
          fontSize: 12,
          color: color.ink,
          textOverflow: 'ellipsis'
        }}
      />
      <button type="button" className="hdr-btn" data-press="" onClick={() => copy(value)} style={SMALL_BUTTON}>
        <span aria-live="polite">{copied ? 'Copied' : 'Copy'}</span>
      </button>
    </div>
  );
}

/**
 * The recovery codes (§37.5): a hairline frame, radius 12, the RECOVERY CODES
 * kicker with « Copy » at its right (all ten, one per line), and the codes in
 * two columns of mono 12.5 at a 1.9 line height.
 */
export function RecoveryCodes({ codes }: { codes: string[] }): React.JSX.Element {
  const [copied, copy] = useCopied();
  return (
    <div style={{ border: `1px solid ${color.line}`, borderRadius: 12, padding: '12px 14px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <span
          style={{ fontFamily: font.mono, fontSize: 10.5, letterSpacing: '.14em', textTransform: 'uppercase', color: color.inkSubtle }}
        >
          Recovery codes
        </span>
        <button type="button" className="hdr-btn" data-press="" onClick={() => copy(codes.join('\n'))} style={SMALL_BUTTON}>
          <span aria-live="polite">{copied ? 'Copied' : 'Copy'}</span>
        </button>
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          columnGap: 16,
          marginTop: 8,
          fontFamily: font.mono,
          fontSize: 12.5,
          lineHeight: 1.9,
          color: color.ink,
          userSelect: 'all'
        }}
      >
        {codes.map((one) => (
          <span key={one}>{one}</span>
        ))}
      </div>
    </div>
  );
}
